import React, { useState, useRef, useEffect, useReducer, useRef as useReactRef } from 'react';
import { Box, Modal } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeContext } from '../../hook/useThemeManager';
import { Form, IFormProps } from './Form';
import { IFormGeneratorWithoutModalRef } from '../FormGenerator/FormGeneratorWithoutModal';
import { useSelector } from 'react-redux';
import { createSystemFormSelector, creatingWorkProfileSelector } from '../../selectors/selectors';
import { initialState, reducer } from './reducer/genericForm.reducer';
import { TSearchAddCategory } from '../../types/global/forms.types';
import { isEmpty } from '../../utils/lodash';
import profileData from '../../data/mock/workProfile.json';
import { FORM_IDENTIFIER_ENUM } from '../../types/global/helper';
import { createTalentProfile } from '../../redux/talentProfile/action';
import { useDispatch } from 'react-redux';
import { parseProfile } from '../../utils/AppUtils';
import { getSystemForms, clearFormDraft } from '../../redux/appConfigurator/action';
import { createCastingDirector } from '../../redux/audition/actions';
import {
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import { EDITORIAL } from '../FormGenerator/editorialFormStyles';

export interface GenericFormFillerProps extends Partial<IFormProps> {
    open: boolean;
    onClose: () => void;
    /** The initial form name that should be set when opening this modal */
    initialStateForm: string;
    /** The below will  be used to trigger save when we don't want to save direcly here */
    onManualSave?: (payload: any) => void;
    manualSave?: boolean;
    /** Optional UI variant override for route-specific theming */
    uiVariant?: 'default' | 'brand';
}

type TView = 'post' | 'audience' | 'photo' | 'tag' | 'color' | 'background' | 'location' | 'more' | 'role' | 'form';

/**
 *
 * @param open
 * @param onClose
 * @param initialStateForm
 * @returns JSX.Element
 *
 * This component is designed to handle all forms in the application
 * it takes the name of the form and renders it using the formGenerator
 *
 * ENSURE that the form name is aailable from the service and has been
 * fetched before loading.
 */

const GenericFormFiller = React.forwardRef<IFormGeneratorWithoutModalRef, GenericFormFillerProps>(
    ({ open, onClose, initialStateForm, onManualSave, manualSave, uiVariant = 'default', ...props }, ref) => {
        const [activeView, setActiveView] = useState<TView>('form');
        const [selectedForm, setSelectedForm] = useState<string | undefined>(initialStateForm);
        const [state, dispatch] = useReducer(reducer, initialState);
        const [historyStack, setHistoryStack] = useState<Array<{ view: TView; form?: string }>>([]);
        const [isEdit, setIsEdit] = useState(false);
        const [initialEditValue, setInitialEditValue] = useState<any>();
        const creatingWorkProfile = useSelector(creatingWorkProfileSelector);
        const [, setEditIndex] = useState<any>();
        const sagaDispatch = useDispatch();

        // Draft management state
        const formRef = useReactRef<IFormGeneratorWithoutModalRef>(null);

        // Use the forwarded ref or fallback to internal ref
        const actualRef = ref || formRef;

        // Example: Filter system forms by a specific type
        const systemFormsByTypeSelector = createSystemFormSelector(form => form.identifier === selectedForm);
        // Example: Use in a component
        const specificSystemForms = useSelector(systemFormsByTypeSelector);
        const containerRef = useRef<HTMLDivElement | null>(null);
        const nextViewRef = useRef<HTMLDivElement | null>(null);
        const [height, setHeight] = useState<number | undefined>(undefined);
        const { device } = useThemeContext();

        /** Handle All Change activities */
        const handleUpdateForm = (key: string, payload: any) => {
            dispatch({
                type: 'FIELD',
                field: key,
                value: payload,
            });
        };

        // Adjust the container's height immediately before changing the active view
        const updateHeight = () => {
            if (nextViewRef.current) {
                requestAnimationFrame(() => {
                    setHeight(nextViewRef.current!.scrollHeight + 50);
                });
            }
        };

        useEffect(() => {
            // Update the initial height for the first render
            if (containerRef.current) {
                setHeight(containerRef.current.scrollHeight + 50);
            }
        }, [activeView]);

        const variants = {
            initial: { opacity: 0, x: 50 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -50 },
        };

        const handleAuditionFormNavigation = () => {
            /** Change these */
            setHistoryStack(prevStack => {
                if (prevStack.length > 0) {
                    const lastEntry = prevStack[prevStack.length - 1];
                    setActiveView(lastEntry.view);
                    setSelectedForm(lastEntry.form);
                    return prevStack.slice(0, -1); // Remove the last entry
                } else {
                    onClose();
                }
                return prevStack;
            });
        };

        const handleHistory = (
            newView: TView,
            formId?: string,
            storekey?: string,
            currentFormState?: any,
            index?: number,
        ) => {
            setHistoryStack(prevStack => [...prevStack, { view: activeView, form: selectedForm }]);
            setActiveView(newView);
            setSelectedForm(formId);
            /** Handle autosve hat we have already */
            if (currentFormState) {
                let payload = {
                    ...state,
                };

                if (specificSystemForms?.parent_key && specificSystemForms.linked_form_field) {
                    const parentKeyToUser = specificSystemForms?.parent_key;
                    const subKey = specificSystemForms.linked_form_field.store_key;
                    Object.assign(payload, {
                        [parentKeyToUser]: {
                            [subKey]: specificSystemForms.multiple
                                ? [...state?.[parentKeyToUser as keyof {}]?.[subKey], currentFormState]
                                : currentFormState,
                        },
                    });
                }
                if (specificSystemForms?.parent_key && !specificSystemForms.linked_form_field) {
                    const parentKeyToUser = specificSystemForms?.parent_key;
                    // Ensure the value is an array or default to an empty array
                    const existingData = Array.isArray(state?.[parentKeyToUser as keyof {}])
                        ? state?.[parentKeyToUser as keyof {}]
                        : [];
                    Object.assign(payload, {
                        [parentKeyToUser]: specificSystemForms.multiple
                            ? [...existingData, currentFormState]
                            : currentFormState,
                    });
                }
                if (!specificSystemForms?.parent_key) {
                    Object.assign(payload, currentFormState);
                }
                if (index) setEditIndex(index);

                handleUpdateForm(storekey!, payload);
            }
        };

        const handleFormSubmit = (formData: any) => {
            const { body, uploads } = parseProfile(formData);
            /** Handle different form groups, see implementation for create work profile */
            switch (selectedForm) {
                case FORM_IDENTIFIER_ENUM.CREATE_WORK_PROFILE:
                    if (manualSave && onManualSave) {
                        onManualSave(body);
                    } else {
                        sagaDispatch(createTalentProfile({ body, uploads }));
                        /** This should refetch the form again should there be changes that user should no longer
                         * have access to
                         */
                        sagaDispatch(getSystemForms({ tag: 'system' }));
                    }
                    // Clear draft on successful save
                    if (selectedForm) {
                        sagaDispatch(clearFormDraft({ formType: selectedForm }));
                    }
                    onClose();
                    break;
                case FORM_IDENTIFIER_ENUM.CREATE_PRODUCTION_COMPANY:
                    sagaDispatch(createCastingDirector(formData));
                    sagaDispatch(getSystemForms({ tag: 'system' }));
                    // Clear draft on successful save
                    if (selectedForm) {
                        sagaDispatch(clearFormDraft({ formType: selectedForm }));
                    }
                    onClose();
                    break;
                default:
                    break;
            }
        };

        const handleOnAddClick = (category?: TSearchAddCategory, formId?: string, currentState?: any) => {
            if (formId) {
                switch (category) {
                    case 'castingDirector':
                        handleHistory('form', formId, undefined, currentState);
                        setSelectedForm(formId);
                        break;
                }
            }
        };

        const handleSaveDraftAndClose = () => {
            if (actualRef && 'current' in actualRef && actualRef.current) {
                // Get current form data and save it as draft
                const currentFormData = actualRef.current.getCurrentFormData();
                actualRef.current.saveDraftAndClose(currentFormData);
            }
        };

        const handleOnSubmit = () => {
            if (actualRef && 'current' in actualRef && actualRef.current) {
                actualRef.current.handleSubmit();
            }
        };

        const handleDisableButton = () => {
            if (actualRef && 'current' in actualRef && actualRef.current) {
                return actualRef.current.disableButton();
            }
        };

        const handleCloseWithoutSavingDraft = () => {
            onClose && onClose();
        };

        const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
        const isBrandVariant = uiVariant === 'brand';

        const getCurrentFormState = () => {
            if (actualRef && 'current' in actualRef && actualRef.current) {
                // Get current form data and save it as draft
                const currentFormData = actualRef.current.getCurrentFormData();
                return currentFormData;
            }
            return {};
        };

        return (
            <Modal open={open} onClose={handleCloseWithoutSavingDraft}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh',
                        backgroundColor: isBrandVariant ? 'rgba(6, 6, 6, 0.78)' : 'rgba(0, 0, 0, 0.5)',
                    }}>
                    <motion.div
                        animate={{ height: height || 'auto' }}
                        transition={{ duration: 0.3 }}
                        style={{
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            /* Horizontal gutter so focus rings / slider thumbs aren’t clipped by the scrollport */
                            ...(isBrandVariant
                                ? { paddingLeft: 14, paddingRight: 14, boxSizing: 'border-box' as const }
                                : {}),
                            maxWidth: device.isMobile ? '100vw' : '800px',
                            width: '100%',
                            backgroundColor: EDITORIAL.background,
                            borderRadius: device.isMobile ? 0 : '16px',
                            boxShadow: isBrandVariant
                                ? '0px 18px 46px rgba(0, 0, 0, 0.6)'
                                : '0px 10px 30px rgba(0, 0, 0, 0.2)',
                            border: isBrandVariant ? `1px solid ${BRAND_OUTLINE_VARIANT}55` : 'none',
                            color: 'white',
                            maxHeight: device.isMobile ? '100dvh' : '800px',
                        }}>
                        <Box
                            ref={containerRef}
                            sx={{
                                boxSizing: 'border-box',
                                maxWidth: '100%',
                                px: { xs: 3, sm: 5 },
                                py: { xs: 3, sm: 3.25 },
                                ...(isBrandVariant
                                    ? {
                                          '& .MuiPaper-root': {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              backgroundImage: 'none',
                                          },
                                          '& .MuiFilledInput-root': {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                          },
                                          '& .MuiFilledInput-root:hover': {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                          },
                                          '& .MuiFilledInput-root.Mui-focused': {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                          },
                                          '& .MuiFormLabel-root, & .MuiInputLabel-root, & .MuiTypography-body2': {
                                              color: 'rgba(255,255,255,0.55)',
                                          },
                                          '& footer': {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                                          },
                                      }
                                    : {}),
                            }}>
                            <AnimatePresence mode="wait">
                                {activeView === 'form' && (
                                    <motion.div
                                        key="audience"
                                        ref={nextViewRef}
                                        onAnimationStart={updateHeight}
                                        variants={variants}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        transition={{ duration: 0.3 }}>
                                        <Form
                                            ref={actualRef}
                                            uiVariant={isBrandVariant ? 'brand' : 'default'}
                                            onBack={handleAuditionFormNavigation}
                                            onClose={handleCloseWithoutSavingDraft}
                                            handleOnSubmit={handleOnSubmit}
                                            disableButton={handleDisableButton()}
                                            inlineMode
                                            hideGlobalButton={false}
                                            handleSubmitInternally={false}
                                            loading={creatingWorkProfile}
                                            onChange={(key, subKey, value) => {
                                                handleUpdateForm(key, value);
                                            }}
                                            formData={getCurrentFormState()}
                                            manualClose
                                            isTopScreen={isEmpty(historyStack)}
                                            devData={profileData}
                                            onSave={handleAuditionFormNavigation}
                                            form={specificSystemForms}
                                            header={specificSystemForms?.title}
                                            leadingText={specificSystemForms?.form_note}
                                            isEdit={isEdit}
                                            initialEditValue={initialEditValue}
                                            onAddClicked={(category, formId, formState) => {
                                                handleOnAddClick(category, formId, formState);
                                            }}
                                            initialValue={state}
                                            onSubmit={handleFormSubmit}
                                            onCreateNewClicked={(formId, storeKey, currentFormState, index) => {
                                                /** On Add click is handled here */
                                                handleHistory('form', formId, storeKey, currentFormState, index);
                                            }}
                                            onEdit={(actionValue, index, form) => {
                                                setIsEdit(true);
                                                setEditIndex(index);
                                                setInitialEditValue(actionValue);
                                                handleHistory('form', form);
                                            }}
                                            formType={selectedForm}
                                            enableDraftSaving={true}
                                            onUnsavedChangesChange={setHasUnsavedChanges}
                                            hasUnsavedChanges={hasUnsavedChanges}
                                            onSaveDraftAndClose={handleSaveDraftAndClose}
                                            showCloseConfirmation={true}
                                            {...props}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Box>
                    </motion.div>
                </Box>
            </Modal>
        );
    },
);

GenericFormFiller.displayName = 'GenericFormFiller';

export { GenericFormFiller };
